import {
  CHALLENGE_TIMEOUT_MS,
  ChallengeStatus,
  getDocumentChallengeState
} from '@spa/lib/fastly-challenge/challengeState';
import Cookies from 'js-cookie';
import { sessionCookieName } from '../const';

const MINT_PATH = 'session-id/mint';
// Best-effort call - bound it so a slow or stalled attempt can never hang the page (real users on
// flaky networks, or a misconfigured host). On timeout the AbortController aborts the fetch, which
// rejects into the catch below; a later mount/navigation retries.
const MINT_TIMEOUT_MS = 3000;

const base64UrlDecode = (segment: string): string => {
  const padded = segment.replace(/-/g, '+').replace(/_/g, '/');
  return atob(padded);
};

// No signature verification here - this is not a security boundary, only an optimization to
// skip a redundant mint call. The real verification always happens server-side; a tampered
// `exp` can only ever cause the tampering client to skip re-minting and then fail real
// verification later, never grant anyone anything.
const isStillValid = (token: string, nowSeconds: number): boolean => {
  let valid = false;
  const segments = token.split('.');

  if (segments.length === 3) {
    try {
      const payload: unknown = JSON.parse(base64UrlDecode(segments[1]));
      const exp: unknown =
        typeof payload === 'object' && payload !== null
          ? (payload as Record<string, unknown>).exp
          : undefined;
      valid = typeof exp === 'number' && exp > nowSeconds;
    } catch {
      valid = false;
    }
  }

  return valid;
};

const readParsedSessionCookie = (): Record<string, unknown> | null => {
  let parsed: Record<string, unknown> | null = null;
  const rawCookie = Cookies.get(sessionCookieName);

  if (rawCookie !== undefined) {
    try {
      const candidate: unknown = JSON.parse(rawCookie);
      if (typeof candidate === 'object' && candidate !== null) {
        parsed = candidate as Record<string, unknown>;
      }
    } catch {
      parsed = null;
    }
  }

  return parsed;
};

const writeSignedSessionId = (
  parsedSessionCookie: Record<string, unknown>,
  signedSessionId: string
): void => {
  const isSecureContext = window.location.protocol === 'https:';
  const expires = new Date();
  expires.setFullYear(expires.getFullYear() + 1);

  Cookies.set(sessionCookieName, JSON.stringify({ ...parsedSessionCookie, signedSessionId }), {
    expires,
    path: '/',
    sameSite: 'lax',
    secure: isSecureContext
  });
};

// The edge rejects the mint with 403 until the document challenge has completed. Resolves false
// when the challenge fails or times out; the next page load retries.
const waitForUsableChallenge = (): Promise<boolean> =>
  new Promise((resolve) => {
    const state = getDocumentChallengeState();
    let waiting = true;

    const settle = (usable: boolean): void => {
      if (waiting) {
        waiting = false;
        clearTimeout(timer);
        unsubscribe();
        resolve(usable);
      }
    };
    const evaluate = (): void => {
      const snapshot = state.getSnapshot();
      if (snapshot.integrationFailure !== null || snapshot.status === ChallengeStatus.ERROR) {
        settle(false);
      } else if (
        snapshot.enabled === false ||
        (snapshot.enabled === true && snapshot.status === ChallengeStatus.COMPLETE)
      ) {
        settle(true);
      }
    };

    const timer = setTimeout(() => settle(false), CHALLENGE_TIMEOUT_MS);
    const unsubscribe = state.subscribe(evaluate);
    evaluate();
  });

export interface ApolloPublicHeader {
  readonly key: string;
  readonly value: string;
}

/**
 * Checks whether the current `session` cookie already carries a present, unexpired
 * signed session id and, only if it doesn't, mints a fresh one via a dedicated call to
 * Apollo. Deliberately does nothing when there is no session cookie yet at all - creating
 * one from scratch is the page-handler flow's job, not this one; a later call (once a
 * session exists) will pick it up.
 *
 * `apolloPublicHeader` mirrors the same header apolloClientFactory.ts already attaches to
 * every browser GraphQL request (graphqlSettings.publicHost.header) - Apollo's Fastly edge
 * checks it on every path, this one included.
 */
const ensureSignedSessionIdClient = async (
  apolloPublicHost: string,
  apolloPublicHeader: ApolloPublicHeader | null
): Promise<void> => {
  if (typeof window === 'undefined') {
    return;
  }

  let parsedSessionCookie = readParsedSessionCookie();
  if (parsedSessionCookie === null) {
    return;
  }

  const existing = parsedSessionCookie.signedSessionId;
  if (typeof existing === 'string' && isStillValid(existing, Math.floor(Date.now() / 1000))) {
    return;
  }

  // Strip any trailing slash(es) from the host before appending the path. `new URL(path, base)`
  // resolves against the base's *directory*, so a base with a path segment and no trailing slash
  // (e.g. `https://host/apollo`) would silently drop that segment. A plain string join preserves
  // the full host+path for every host shape - path-less (`https://backend...`), path-based
  // (`https://host/apollo`), or already slash-terminated. Trim with a linear scan rather than a
  // `/\/+$/` regex, whose greedy quantifier-before-anchor trips Sonar's ReDoS rule (S5852).
  let trimmedHost = apolloPublicHost;
  while (trimmedHost.endsWith('/')) {
    trimmedHost = trimmedHost.slice(0, -1);
  }
  const mintUrl = `${trimmedHost}/${MINT_PATH}`;

  const challengeUsable = await waitForUsableChallenge();
  if (challengeUsable) {
    const abortController = new AbortController();
    const timeoutId = setTimeout(() => {
      abortController.abort();
    }, MINT_TIMEOUT_MS);

    try {
      const response = await fetch(mintUrl, {
        method: 'POST',
        headers:
          apolloPublicHeader === null
            ? undefined
            : { [apolloPublicHeader.key]: apolloPublicHeader.value },
        signal: abortController.signal
      });

      if (response.ok) {
        const body: unknown = await response.json();
        if (
          typeof body === 'object' &&
          body !== null &&
          typeof (body as Record<string, unknown>).signedSessionId === 'string'
        ) {
          parsedSessionCookie = readParsedSessionCookie();
          if (parsedSessionCookie !== null) {
            writeSignedSessionId(
              parsedSessionCookie,
              (body as { signedSessionId: string }).signedSessionId
            );
          }
        }
      }
    } catch {
      // Network failure or timeout abort - not a critical path, a later mount/navigation will retry.
    } finally {
      clearTimeout(timeoutId);
    }
  }
};

export default ensureSignedSessionIdClient;
